import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  closestCorners,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors
} from "@dnd-kit/core"
import { CSS } from "@dnd-kit/utilities"
import { ActionButton, Button, Loading } from "components"
import moment from "moment"
import { TaskList as TaskListRow } from "pages/Task/types"
import { taskListToSubmitPayload } from "pages/Task/utils/functions"
import { useTaskList } from "pages/Task/utils/hooks"
import { TASK_PRIORITY, TASK_STATUS } from "pages/Task/utils/vars"
import { type ReactNode, useMemo, useState } from "react"
import toast from "react-hot-toast"
import { IoSendSharp } from "react-icons/io5"
import { useApi, useToggle } from "utils"
import { ModalDelete } from "../ModalDelete"
import { ModalTaskDetail } from "../ModalTaskDetail"

type TaskBoardGridProps = {
  tasks: ReturnType<typeof useTaskList>
}

const STATUS_COLUMNS: { status: number; label: string }[] = [
  { status: 1, label: "Planned" },
  { status: 2, label: "In Progress" },
  { status: 3, label: "Under Review" },
  { status: 4, label: "Completed" },
  { status: 5, label: "On Hold" }
]

const COL_PREFIX = "col-"
const TASK_PREFIX = "task-"

function colId(status: number) {
  return `${COL_PREFIX}${status}`
}

function taskDragId(taskId: number) {
  return `${TASK_PREFIX}${taskId}`
}

function parseOverTarget(
  overId: string | number,
  data: TaskListRow[] | undefined
): number | null {
  const s = String(overId)
  if (s.startsWith(COL_PREFIX)) {
    const n = parseInt(s.slice(COL_PREFIX.length), 10)
    return Number.isFinite(n) ? n : null
  }
  if (s.startsWith(TASK_PREFIX)) {
    const taskId = parseInt(s.slice(TASK_PREFIX.length), 10)
    const row = data?.find((t) => t.task_id === taskId)
    return row?.task_status ?? null
  }
  return null
}

function initials(name: string) {
  if (!name?.trim()) return "?"
  return name
    .split(/\s+/)
    .map((w) => w[0])
    .join("")
    .substring(0, 3)
    .toUpperCase()
}

function priorityClass(priority: number) {
  switch (priority) {
    case 1:
      return "bg-rose-100 text-rose-700"
    case 2:
      return "bg-orange-100 text-orange-700"
    case 3:
      return "bg-blue-100 text-blue-700"
    case 4:
      return "bg-green-100 text-green-700"
    default:
      return "bg-base-200 text-base-content"
  }
}

/** Aksen visual prioritas (bukan field terpisah di API — derivasi dari `priority`). */
function PriorityAccentBars({ priority }: { priority: number }) {
  const pairs: Record<number, [string, string]> = {
    1: ["bg-amber-400", "bg-rose-600"],
    2: ["bg-amber-400", "bg-orange-600"],
    3: ["bg-sky-400", "bg-blue-600"],
    4: ["bg-emerald-400", "bg-slate-500"]
  }
  const [a, b] = pairs[priority] ?? ["bg-base-300", "bg-base-400"]
  return (
    <div className="flex gap-1 mb-2 w-20" aria-hidden>
      <div className={`h-1 w-9 rounded-sm ${a}`} />
      <div className={`h-1 w-9 rounded-sm ${b}`} />
    </div>
  )
}

function taskStatusLabel(item: TaskListRow): string {
  const fromColumn = STATUS_COLUMNS.find((c) => c.status === item.task_status)
    ?.label
  if (fromColumn) return fromColumn
  const key = TASK_STATUS[item.task_status]
  return typeof key === "string" ? key : "—"
}

type BoardCardProps = {
  item: TaskListRow
  pending: boolean
  onOpen: () => void
  onDelete: () => void
}

function BoardCardContent({
  item,
  pending,
  onOpen,
  onDelete
}: BoardCardProps) {
  return (
    <>
      <div className="flex items-start justify-between gap-2 mb-2">
        <span className="text-sm font-semibold text-base-content/70">
          {item.trx_code}
        </span>
        <span
          className={`text-[11px] font-bold uppercase rounded-full px-2 py-0.5 whitespace-nowrap shrink-0 tracking-wide ${priorityClass(item.priority)}`}>
          {TASK_PRIORITY[item.priority] ?? "-"}
        </span>
      </div>

      <PriorityAccentBars priority={item.priority} />

      <h3 className="text-lg font-extrabold leading-snug text-base-content mb-3.5 line-clamp-3 tracking-tight">
        {item.subject}
      </h3>

      <dl className="grid grid-cols-2 gap-x-3 gap-y-2.5 mb-3.5 text-base">
        <div className="min-w-0">
          <dt className="uppercase font-bold text-sm text-base-content tracking-wide">
            Department
          </dt>
          <dd className="mt-0.5 truncate font-medium text-base-content/90">
            {item.department_name}
          </dd>
        </div>
        <div className="min-w-0">
          <dt className="uppercase font-bold text-sm text-base-content tracking-wide">
            Date
          </dt>
          <dd className="mt-0.5 font-medium text-base-content/90 tabular-nums">
            {moment(item.date_end).format("YYYY-MM-DD")}
          </dd>
        </div>
        <div className="min-w-0">
          <dt className="uppercase font-bold text-sm text-base-content tracking-wide">
            Status
          </dt>
          <dd className="mt-0.5 truncate font-medium text-base-content/90">
            {taskStatusLabel(item)}
          </dd>
        </div>
        <div className="min-w-0">
          <dt className="uppercase font-bold text-sm text-base-content tracking-wide">
            Owner
          </dt>
          <dd className="mt-0.5 truncate font-medium text-base-content/90">
            {item.owner_name}
          </dd>
        </div>
      </dl>

      <div className="mb-3.5">
        <div className="flex justify-between text-sm font-bold text-base-content mb-1.5">
          <span>Progress</span>
          <span className="tabular-nums font-extrabold text-base-content">
            {parseFloat(item.progress?.toString() ?? "0").toFixed(0)}%
          </span>
        </div>
        <div className="h-1.5 w-full rounded-full bg-base-300 overflow-hidden">
          <div
            className="h-full rounded-full bg-primary transition-all"
            style={{
              width: `${Math.min(100, Math.max(0, Number(item.progress) || 0))}%`
            }}
          />
        </div>
      </div>

      <footer className="flex items-center justify-between gap-2 border-t border-base-200 pt-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-neutral text-sm font-extrabold text-primary">
            {initials(item.owner_name)}
          </span>
          <span className="text-base font-bold text-base-content truncate">
            {item.owner_name}
          </span>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button
            size="xs"
            color="outline-secondary"
            title="Open task"
            className="font-semibold normal-case text-base px-3"
            onClick={(e) => {
              e.stopPropagation()
              onOpen()
            }}>
            <span className="flex items-center gap-1">
              Open
              <IoSendSharp className="size-4" />
            </span>
          </Button>
          {item.candel === 1 ? (
            <ActionButton.Delete
              onClick={(e) => {
                e.stopPropagation()
                onDelete()
              }}
            />
          ) : null}
        </div>
      </footer>

      {pending ? (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-base-100/70">
          <span className="loading loading-spinner loading-sm text-primary" />
        </div>
      ) : null}
    </>
  )
}

function DraggableBoardCard({
  item,
  pending,
  onOpen,
  onDelete
}: BoardCardProps) {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id: taskDragId(item.task_id),
      data: { type: "task", task: item },
      disabled: pending
    })

  const style = transform
    ? { transform: CSS.Translate.toString(transform) }
    : undefined

  return (
    <article
      ref={setNodeRef}
      style={style}
      className={`relative rounded-lg border border-base-300 bg-base-100 p-3 shadow-sm transition hover:shadow-md ${
        isDragging ? "z-20 cursor-grabbing opacity-40 ring-2 ring-primary/40" : "cursor-grab"
      }`}
      {...listeners}
      {...attributes}>
      <BoardCardContent
        item={item}
        pending={pending}
        onOpen={onOpen}
        onDelete={onDelete}
      />
    </article>
  )
}

function DroppableColumnBody({
  status,
  children
}: {
  status: number
  children: ReactNode
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: colId(status),
    data: { type: "column", status }
  })

  return (
    <div
      ref={setNodeRef}
      className={`flex max-h-[min(52vh,calc(100vh-22rem))] min-h-[200px] flex-1 flex-col gap-3 overflow-y-auto p-3 transition-colors ${
        isOver ? "bg-primary/10 ring-2 ring-inset ring-primary/30 rounded-b-lg" : ""
      }`}>
      {children}
    </div>
  )
}

export function TaskBoardGrid({ tasks }: TaskBoardGridProps) {
  const api = useApi()
  const modalDelete = useToggle(false)
  const modalDetail = useToggle(false)
  const [deleteData, setDeleteData] = useState<TaskListRow>({} as TaskListRow)
  const [detailData, setDetailData] = useState<TaskListRow>({} as TaskListRow)
  const [activeTask, setActiveTask] = useState<TaskListRow | null>(null)
  const [pendingTaskId, setPendingTaskId] = useState<number | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 }
    })
  )

  const grouped = useMemo(() => {
    const map: Record<number, TaskListRow[]> = {
      1: [],
      2: [],
      3: [],
      4: [],
      5: []
    }
    for (const row of tasks.data ?? []) {
      const key = row.task_status
      if (map[key]) map[key].push(row)
    }
    return map
  }, [tasks.data])
  const hasBoardData = (tasks.data?.length ?? 0) > 0

  const handleDragStart = (event: DragStartEvent) => {
    const id = String(event.active.id)
    if (!id.startsWith(TASK_PREFIX)) return
    const taskId = parseInt(id.slice(TASK_PREFIX.length), 10)
    const row = tasks.data?.find((t) => t.task_id === taskId)
    setActiveTask(row ?? null)
  }

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event
    setActiveTask(null)

    if (!over) return

    const activeId = String(active.id)
    if (!activeId.startsWith(TASK_PREFIX)) return

    const taskId = parseInt(activeId.slice(TASK_PREFIX.length), 10)
    const row = tasks.data?.find((t) => t.task_id === taskId)
    if (!row) return

    const targetStatus = parseOverTarget(over.id, tasks.data)
    if (targetStatus === null || row.task_status === targetStatus) return

    setPendingTaskId(taskId)
    try {
      await api.put(
        `/task/${row.task_id}`,
        taskListToSubmitPayload(row, targetStatus)
      )
      toast.success("Status updated")
      tasks.refetch()
    } catch (err: unknown) {
      const ax = err as {
        response?: { data?: { detail?: { message?: string }; message?: string } }
      }
      toast.error(
        ax.response?.data?.detail?.message ??
          ax.response?.data?.message ??
          "Failed to update status"
      )
    } finally {
      setPendingTaskId(null)
    }
  }

  const handleDragCancel = () => {
    setActiveTask(null)
  }

  if (tasks.isLoading && !hasBoardData) {
    return (
      <div className="min-h-[44vh] flex items-center justify-center rounded-xl border border-base-300 bg-base-100">
        <div className="text-center">
          <Loading loading />
          <p className="mt-2 text-xs font-medium text-base-content/60">
            Loading board tasks...
          </p>
        </div>
      </div>
    )
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}>
      <div className="relative w-full min-w-0">
        <div className="grid w-full min-w-0 grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
          {STATUS_COLUMNS.map((col) => {
            const list = grouped[col.status] ?? []
            return (
              <section
                key={col.status}
                className="flex min-h-[420px] min-w-0 flex-col rounded-xl border border-base-300 bg-base-300/70 shadow-sm">
                <header className="flex items-center justify-between gap-2 rounded-t-xl bg-black text-white px-3 py-2.5 text-xs font-bold uppercase tracking-wide">
                  <span className="truncate">{col.label}</span>
                  <span className="shrink-0 rounded-full bg-primary px-2 py-0.5 text-[11px] font-extrabold text-black">
                    {list.length}
                  </span>
                </header>

                <DroppableColumnBody status={col.status}>
                  {list.length === 0 ? (
                    <p className="text-center text-xs text-base-content/50 py-6 pointer-events-none">
                      Drop tasks here
                    </p>
                  ) : (
                    list.map((item) => (
                      <DraggableBoardCard
                        key={item.task_id}
                        item={item}
                        pending={pendingTaskId === item.task_id}
                        onOpen={() => {
                          setDetailData(item)
                          modalDetail.setActive(true)
                        }}
                        onDelete={() => {
                          setDeleteData(item)
                          modalDelete.setActive(true)
                        }}
                      />
                    ))
                  )}
                </DroppableColumnBody>
              </section>
            )
          })}
        </div>
        {tasks.isLoading ? (
          <div className="pointer-events-none absolute inset-0 z-10 flex items-start justify-center rounded-xl bg-base-100/45 pt-6 backdrop-blur-[1px]">
            <div className="rounded-full border border-base-300 bg-base-100/95 px-3 py-1.5 shadow-sm">
              <span className="loading loading-spinner loading-xs mr-2 align-middle" />
              <span className="text-xs font-semibold text-base-content/70">
                Updating board...
              </span>
            </div>
          </div>
        ) : null}
      </div>

      <DragOverlay dropAnimation={{ duration: 200, easing: "ease" }}>
        {activeTask ? (
          <div className="w-[260px] max-w-[85vw] cursor-grabbing rounded-lg border-2 border-primary bg-base-100 p-3 shadow-xl opacity-95">
            <div className="flex items-start justify-between gap-2 mb-1.5">
              <span className="text-sm font-semibold text-base-content/70">
                {activeTask.trx_code}
              </span>
              <span
                className={`text-[11px] font-bold uppercase rounded-full px-2 py-0.5 whitespace-nowrap shrink-0 tracking-wide ${priorityClass(activeTask.priority)}`}>
                {TASK_PRIORITY[activeTask.priority] ?? "-"}
              </span>
            </div>
            <PriorityAccentBars priority={activeTask.priority} />
            <h3 className="text-lg font-extrabold leading-snug text-base-content line-clamp-3 tracking-tight">
              {activeTask.subject}
            </h3>
          </div>
        ) : null}
      </DragOverlay>

      <ModalDelete
        data={deleteData}
        modalState={modalDelete}
        onSuccess={() => tasks.refetch()}
      />
      <ModalTaskDetail
        data={detailData}
        isOpen={modalDetail.isActive}
        onClose={modalDetail.toggle}
        onSuccess={() => tasks.refetch()}
      />
    </DndContext>
  )
}
